
const RaisingNextGenerationOfFaith = () => {
  return (
    <div className="bg-[#fff] w-full flex flex-col">
      <div
        className="relative w-full bg-cover bg-center h-[550px] flex items-center justify-center"
        style={{
          backgroundImage: "url('./images/raising-next-generation.png')",
        }}
      >
        <div className="absolute inset-0 bg-black/60" />
        <div className="relative text-center px-6 md:px-12 lg:px-20 max-w-4xl">
          <h1 className="text-white text-3xl md:text-5xl font-bold leading-tight mb-4">
            Raising the Next Generation of Faith Through Sunday School
          </h1>
          <p className="text-gray-200 text-lg md:text-xl">
            Explore how Sunday School continues to nurture faith, character, and leadership in young hearts today.
          </p>
        </div>
      </div>

      <section className="body--container w-full px-4 py-10 md:px-12 lg:px-24 xl:px-32">
        <div className="mx-auto max-w-4xl text-gray-800 leading-relaxed">
          <p className="mb-6">
            In every generation, the responsibility of passing on faith, values, and moral strength falls to those who came before.
            Parents, grandparents, and church leaders share a common hope — that the next generation will not only believe but also
            live out their faith with conviction, compassion, and courage.
          </p>

          <p className="mb-6">
            In today’s fast-changing world, where children are constantly influenced by technology, social trends, and competing
            ideologies, this mission can feel more challenging than ever. Yet, there’s one timeless institution that continues to
            nurture faith effectively across generations — Sunday School.
          </p>

          <p className="mb-6">
            Let’s explore how Sunday School remains a cornerstone in raising the next generation of faith and why it’s more relevant
            today than ever before.
          </p>

          <div className="mt-8">
            <h2 className="text-[#000] text-2xl font-semibold mb-3 pl-4 border-l-4 border-primary">
              1. Planting Seeds of Faith Early
            </h2>
            <p>
              The foundation of a strong faith journey often begins in childhood. Sunday School provides children with their first
              encounter with Scripture, helping them understand God’s love, grace, and purpose for their lives. Through stories, songs,
              and activities, early lessons are planted like seeds that grow into lifelong faith when nurtured consistently.
            </p>
          </div>

          <div className="mt-8">
            <h2 className="text-[#000] text-2xl font-semibold mb-3 pl-4 border-l-4 border-primary">
              2. Making Faith Practical and Relatable
            </h2>
            <p>
              Today’s children need to know not only what to believe but why it matters. Sunday School bridges that gap with
              age-appropriate lessons, discussions, and examples that show how biblical principles apply to real-life situations —
              kindness, honesty, respect, and forgiveness.
            </p>
          </div>

          <div className="mt-8">
            <h2 className="text-[#000] text-2xl font-semibold mb-3 pl-4 border-l-4 border-primary">
              3. Encouraging Critical Thinking About Faith
            </h2>
            <p>
              Sunday School encourages children to ask honest questions about life, God, and faith — and receive thoughtful answers.
              This fosters a personal relationship with God, helping them stand firm in faith despite doubt, peer pressure, or
              cultural challenges.
            </p>
          </div>

          <div className="mt-8">
            <h2 className="text-[#000] text-2xl font-semibold mb-3 pl-4 border-l-4 border-primary">
              4. Fostering Strong Role Models and Mentorship
            </h2>
            <p>
              Teachers serve as faith mentors, modeling what living faith looks like — through words, actions, and service. These
              role models leave lasting impressions, showing children that faith is woven into daily life.
            </p>
          </div>

          <div className="mt-8">
            <h2 className="text-[#000] text-2xl font-semibold mb-3 pl-4 border-l-4 border-primary">
              5. Building a Sense of Belonging and Community
            </h2>
            <p>
              Sunday School provides a supportive environment where children grow in faith alongside peers. Shared activities and
              collaborative projects create a sense of community, teaching them that the Church is not just a building but a family of
              believers.
            </p>
          </div>

          <div className="mt-8">
            <h2 className="text-[#000] text-2xl font-semibold mb-3 pl-4 border-l-4 border-primary">
              6. Equipping the Next Generation to Serve
            </h2>
            <p>
              Faith is about action. Sunday School helps children live out God’s Word through service — from helping the needy to
              participating in community projects. Early practice in giving back nurtures compassionate, service-minded future leaders.
            </p>
          </div>

          <div className="mt-8">
            <h2 className="text-[#000] text-2xl font-semibold mb-3 pl-4 border-l-4 border-primary">
              7. Strengthening Families Through Shared Faith
            </h2>
            <p>
              Lessons learned in Sunday School often inspire conversations and prayer at home. This shared spiritual journey strengthens
              family bonds and bridges generations, creating a supportive faith ecosystem.
            </p>
          </div>

          <div className="mt-8">
            <h2 className="text-[#000] text-2xl font-semibold mb-3 pl-4 border-l-4 border-primary">
              8. Adapting Faith Education for a Modern World
            </h2>
            <p>
              Modern Sunday School programs incorporate technology, multimedia, and interactive methods. From apps to crafts, these
              tools make faith education engaging and accessible for today’s digital-native children.
            </p>
          </div>

          <div className="mt-8">
            <h2 className="text-[#000] text-2xl font-semibold mb-3 pl-4 border-l-4 border-primary">
              9. Empowering Future Leaders of the Church
            </h2>
            <p>
              By participating in reading, leading prayers, or mentoring younger students, children develop confidence, leadership
              skills, and spiritual maturity — preparing them to actively contribute to the Church and community.
            </p>
          </div>

          <div className="mt-8">
            <h2 className="text-[#000] text-2xl font-semibold mb-3 pl-4 border-l-4 border-primary">
              Conclusion: Passing the Torch of Faith
            </h2>
            <p>
              Raising the next generation of faith is a lifelong investment. Sunday School provides structure, guidance, and
              community to nurture lasting faith, equipping children to navigate life with grace and share God’s love.
            </p>
          </div>
        </div>
      </section>

      <section className="mb-12">
        <div className="max-w-4xl mx-auto flex flex-col lg:flex-row justify-between items-center gap-6">
          <div className="flex flex-wrap gap-2">
            <p className="bg-gray-200 text-gray-600 px-3 py-1 rounded-md">Sunday School</p>
            <p className="bg-gray-200 text-gray-600 px-3 py-1 rounded-md">Faith Education</p>
            <p className="bg-gray-200 text-gray-600 px-3 py-1 rounded-md">Christian Leadership</p>
            <p className="bg-gray-200 text-gray-600 px-3 py-1 rounded-md">Community Engagement</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default RaisingNextGenerationOfFaith;